import { useEffect, useState } from "react";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

// R13 Workstream 3 - what ACTUALLY happened. Every stage is counted on its own event date and carries its
// definition, so a number can never be read as something it is not (the cohort funnel below buckets by the
// date the OPPORTUNITY was created and counts attempt records that never reached the provider).
export const LEDGER_ORDER = [
  ["intake_rows", "Intake rows"], ["opportunities_created", "Opportunities created"],
  ["invitations_created", "Invitations created"], ["voice_chases_created", "Voice chases created"],
  ["queued_attempts", "Queued attempts"], ["provider_dispatches", "Provider dispatches"],
  ["provider_acceptances", "Provider acceptances"], ["provider_results", "Provider results"],
  ["bookings", "Bookings"], ["human_handovers", "Human handovers"], ["technical_failures", "Technical failures"],
  ["customer_call_attempts", "Customer call attempts"], ["infrastructure_retries", "Infrastructure retries"],
];

export const ledgerRows = (data) => (data?.events ? LEDGER_ORDER.map(([key, label]) => ({
  key, label, count: data.events[key]?.count ?? 0, definition: data.events[key]?.definition || "",
  dateField: data.events[key]?.date_field || "",
  extra: key === "human_handovers" && data.events[key]
    ? `${data.events[key].customer_driven} customer-driven · ${data.events[key].technical} technical` : null,
})) : []);

export const VoiceFunnelLedger = ({ params }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    api.get(`/reports/voice-automation/funnel?${new URLSearchParams(params).toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(errDetail(e, "Could not load the funnel ledger")); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);

  if (error) return <Card><CardContent className="p-3 text-xs text-[hsl(var(--danger))]">{error}</CardContent></Card>;
  if (!data) return <Skeleton className="h-40" />;
  const rows = ledgerRows(data);
  const cohort = data.attempt_cohort;
  return (
    <Card data-testid="voice-funnel-ledger">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          What actually happened
          <Badge variant="outline" className="text-[10px]" data-testid="ledger-reconciles">
            {data.reconciles ? "reconciles" : "DOES NOT RECONCILE"}
          </Badge>
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Each stage is counted on its own event date. Technical retries are never customer call attempts.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground">
              <th className="py-1 pr-3">Stage</th><th className="py-1 pr-3 text-right">Count</th><th className="py-1">Definition</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t align-top" data-testid={`ledger-row-${r.key}`}>
                  <td className="py-1 pr-3 whitespace-nowrap font-medium">{r.label}</td>
                  <td className="py-1 pr-3 text-right tabular-nums" data-testid={`ledger-count-${r.key}`}>{r.count}</td>
                  <td className="py-1 text-muted-foreground">{r.definition}{r.extra ? ` (${r.extra})` : ""}
                    <span className="block text-[10px] opacity-70">{r.dateField}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded border p-2 text-xs space-y-1" data-testid="ledger-cohort">
          <p className="font-medium">Attempts created in this window, followed to the end</p>
          <p>{cohort.attempts_created} created · {cohort.dispatch_attempted} dispatch attempted · {cohort.accepted_by_provider} accepted by the
            provider · {cohort.provider_results} results · {cohort.never_reached_provider} never reached the provider ·{" "}
            {cohort.infrastructure_retries} infrastructure retries</p>
          <p className="text-muted-foreground">Reached the provider: {cohort.rates.reached_provider_pct ?? "—"}% of attempts created ·
            result received: {cohort.rates.result_received_pct_of_accepted ?? "—"}% of accepted</p>
        </div>
        <ul className="text-[11px] space-y-0.5" data-testid="ledger-checks">
          {data.reconciliation.map((c) => (
            <li key={c.check} className={c.ok ? "text-muted-foreground" : "text-[hsl(var(--danger))] font-medium"}>
              {c.ok ? "✓" : "✗"} {c.check}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
